import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Play } from "lucide-react";
import { fileUrl } from "../lib/api";
import api from "../lib/api";

/**
 * IntroVideoButton — botão "Assistir ao vídeo" + modal com player.
 *
 * Onda A · Etapa A3.
 * - Só renderiza se house.intro_video existir.
 * - O <video src=""> só é montado quando o modal é aberto (lazy).
 * - Telemetria mínima: POST /api/public/videos/telemetry ao abrir.
 * - Poster: intro_video.poster_path > cover photo > primeiro frame nativo.
 * - Sem autoplay; muted; playsInline; controls; preload=metadata.
 */
export default function IntroVideoButton({ house }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const videoRef = useRef(null);

  const video = house?.intro_video;
  if (!video) return null;

  const cover = (house.photos || []).find((p) => p.is_cover) || (house.photos || [])[0];
  const posterUrl = video.poster_path
    ? fileUrl(video.poster_path)
    : cover?.storage_path
      ? fileUrl(cover.storage_path)
      : undefined;

  const openModal = async () => {
    setOpen(true);
    // Telemetria: dispara sem bloquear a UI. Falha silenciosa.
    try {
      await api.post("/public/videos/telemetry", {
        house_id: house.id,
        video_id: video.id,
        event: "opened",
      });
    } catch (_err) {
      // não expor erro ao usuário
    }
  };

  const closeModal = () => {
    // Pausa o vídeo antes de fechar (garante que áudio/CPU liberam)
    try {
      videoRef.current?.pause();
    } catch (_e) { /* noop */ }
    setOpen(false);
    // Retorno de foco ao botão (a11y)
    setTimeout(() => buttonRef.current?.focus(), 30);
  };

  const onEnded = async () => {
    // Telemetria opcional de conclusão
    try {
      await api.post("/public/videos/telemetry", {
        house_id: house.id,
        video_id: video.id,
        event: "completed",
        watched_seconds: videoRef.current?.currentTime ?? null,
      });
    } catch (_err) { /* noop */ }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-[#0AA89C] hover:text-[#0AA89C] hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0AA89C]/60"
        data-testid="watch-video-btn"
        aria-label={`Assistir ao vídeo de apresentação de ${house.name}`}
      >
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0AA89C] text-white">
          <Play className="h-3.5 w-3.5 fill-white translate-x-[1px]" />
        </span>
        Assistir ao vídeo
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeModal())}>
        <DialogContent
          className="bg-black border-none p-0 max-w-4xl w-[95vw] overflow-hidden"
          data-testid="video-modal"
        >
          <DialogTitle className="sr-only">Vídeo de {house.name}</DialogTitle>
          <DialogDescription className="sr-only">
            Reprodução do vídeo de apresentação do imóvel {house.name}. Pressione Esc para fechar.
          </DialogDescription>
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <div className="text-white text-sm truncate pr-4" data-testid="video-modal-title">
              {house.name}
            </div>
            {/* botão Fechar do Dialog do shadcn é injetado via DialogContent (X no topo direito).
                Mantemos apenas o título aqui para satisfazer o requisito de layout mínimo. */}
          </div>
          {open && (
            <video
              ref={videoRef}
              className="block w-full max-h-[80vh] bg-black"
              src={fileUrl(video.path)}
              poster={posterUrl}
              controls
              muted
              playsInline
              preload="metadata"
              onEnded={onEnded}
              data-testid="intro-video-player"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
